import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["wrapperStyle", "onChange", "initialCountry", "isValid", "value", "disabled", "checkmarkTop", "checkmarkRight", "onAutoFill", "placeholder", "onBlur"];
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';
import { PropTypes } from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { background, border, color, space, typography } from 'styled-system';
import { Flex } from '../../Layout';
import COUNTRIES from '../countries.json';
import { ValidCheckmark } from '../ValidCheckmark';
import { CountrySelector } from './CountrySelector';
const duration = 200;
const InputWrapper = styled(Flex).withConfig({
  displayName: "InputWrapper",
  componentId: "sc-3ipeuz-0"
})(["&&&{position:relative;justify-content:space-between;align-items:center;width:100%;}"]);
const StyledInput = styled.input.withConfig({
  displayName: "StyledInput",
  componentId: "sc-3ipeuz-1"
})(["&&&{@keyframes onAutoFillSelected{from{}}", " ", " ", " ", " ", " border:none;width:100%;font-size:inherit;line-height:inherit;font-weight:900;background-color:inherit;transition:padding ", "ms ease-in;::placeholder{font-weight:400;}:-internal-autofill-selected{animation-name:onAutoFillSelected;transition:background-color 50000s ease-in-out 0s;}}"], background, border, color, space, typography, duration);
const StyledSpan = styled.span.withConfig({
  displayName: "StyledSpan",
  componentId: "sc-3ipeuz-2"
})(["margin-right:8px;font-size:16px;line-height:1.5;color:#b6babd;cursor:", ";"], p => p.disabled ? 'default' : 'text');
const StyledDiv = styled.div.withConfig({
  displayName: "StyledDiv",
  componentId: "sc-3ipeuz-3"
})(["display:flex;width:100%;align-items:center;margin-right:12px;"]);
export const PhoneInput = React.forwardRef(function PhoneInput(props, ref) {
  const {
      wrapperStyle,
      onChange,
      initialCountry,
      isValid,
      value,
      disabled,
      checkmarkTop,
      checkmarkRight,
      onAutoFill,
      placeholder,
      onBlur
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const [country, setCountry] = useState(() => {
    var _parsePhoneNumberFrom;
    const phoneNumberCountry = (_parsePhoneNumberFrom = parsePhoneNumberFromString(value)) === null || _parsePhoneNumberFrom === void 0 ? void 0 : _parsePhoneNumberFrom.country;
    return phoneNumberCountry ? COUNTRIES[phoneNumberCountry] : COUNTRIES[initialCountry];
  });
  const [phoneValue, setPhoneValue] = useState(value);
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const asYouType = new AsYouType();
  const onAutoFillSelected = el => {
    const phone = el.value;
    const parsedPhone = parsePhoneNumberFromString(phone);
    if (!!parsedPhone && parsedPhone !== null && parsedPhone !== void 0 && parsedPhone.isValid()) {
      if (country.id !== parsedPhone.country) {
        setCountry(COUNTRIES[parsedPhone.country]);
      }
      asYouType.reset();
      setPhoneValue(asYouType.input(phone));
      onAutoFill(phone);
    } else {
      setPhoneValue(country.dialCode);
      onAutoFill(country.dialCode);
    }
  };
  const onAnimationStart = _ref => {
    let {
      target,
      animationName
    } = _ref;
    switch (animationName) {
      case 'onAutoFillSelected':
        return onAutoFillSelected(target);
      default:
        break;
    }
  };
  useEffect(() => {
    if (ref.current.addEventListener) ref.current.addEventListener('animationstart', onAnimationStart, false);
  }, [ref]);
  const handleCountryChange = selection => {
    setCountry(selection);
    setPhoneValue(selection.dialCode);
    onChange(selection.dialCode);
  };
  const handlePhoneChange = e => {
    const phone = e.target.value.toString();
    if (phone.length < country.dialCode.length) {
      e.preventDefault();
      return;
    }
    asYouType.reset();
    const formatedPhone = asYouType.input(phone);
    setPhoneValue(formatedPhone);
    onChange(formatedPhone);
  };
  useEffect(() => {
    setShowPlaceholder(!isFocused && (phoneValue === country.dialCode || phoneValue === ''));
  }, [phoneValue, country.dialCode, phoneValue, isFocused]);
  const handleFocus = () => {
    setIsFocused(true);
  };
  const handleBlur = e => {
    setIsFocused(false);
    onBlur(e);
  };
  const handleClickPlaceholder = () => {
    if (!disabled) {
      setShowPlaceholder(false);
      ref.current.focus();
    }
  };
  return React.createElement(React.Fragment, null, React.createElement(InputWrapper, wrapperStyle, React.createElement(CountrySelector, {
    countries: COUNTRIES,
    iso: country.id,
    onSelect: handleCountryChange,
    disabled: disabled
  }), React.createElement(StyledDiv, null, React.createElement(StyledInput, _extends({
    ref: ref,
    autoComplete: "tel-national",
    type: "tel",
    color: "inherit",
    onChange: handlePhoneChange,
    onFocus: handleFocus,
    onBlur: e => handleBlur(e),
    name: "phone",
    isValid: isValid,
    value: phoneValue || COUNTRIES[initialCountry].dialCode,
    disabled: disabled,
    onAnimationStart: onAnimationStart
  }, rest)), showPlaceholder && placeholder && React.createElement(StyledSpan, {
    disabled: disabled,
    onClick: () => handleClickPlaceholder()
  }, placeholder)), React.createElement(ValidCheckmark, {
    isValid: isValid,
    top: checkmarkTop,
    right: checkmarkRight
  })));
});
PhoneInput.propTypes = process.env.NODE_ENV !== "production" ? {
  isValid: PropTypes.bool,
  wrapperStyle: PropTypes.objectOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  initialCountry: PropTypes.string,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  checkmarkRight: PropTypes.string,
  checkmarkTop: PropTypes.string,
  onAutoFill: PropTypes.func,
  onBlur: PropTypes.func
} : {};
PhoneInput.defaultProps = {
  isValid: false,
  wrapperStyle: {
    border: 'brandDark',
    borderRadius: '24px',
    backgroundColor: 'background.spaceWhite',
    padding: '13px 18px',
    fontSize: '17px',
    lineHeight: '21px'
  },
  initialCountry: 'ES',
  disabled: false,
  onBlur: () => {}
};